import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { movies, categories } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movieId = parseInt(id || '0', 10);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const movie = movies.find((m) => m.id === movieId) as MovieItem | undefined;

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading text-foreground-900 mb-2">影片未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catInfo = categories.find((c) => c.slug === movie.category);
  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.poster,
    dateCreated: `${movie.year}-01-01`,
    director: { '@type': 'Person', name: movie.director },
    actor: movie.cast.map((name) => ({ '@type': 'Person', name })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.90cinema.com/' },
      { '@type': 'ListItem', position: 2, name: catInfo?.name || '', item: `https://www.90cinema.com/category/${movie.category}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <article className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-2xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${movie.category}`} className="hover:text-primary-500 transition-colors">
                {catInfo?.name}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium">{movie.title}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              <div className="w-full lg:w-72 flex-shrink-0">
                <div className="rounded-lg overflow-hidden aspect-[2/3] lg:sticky lg:top-24">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    title={`${movie.title} - 90影院高清在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 mb-3">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="flex items-center gap-1 px-2.5 py-1 bg-accent-100 text-accent-700 text-sm rounded-full">
                    <i className="ri-star-fill"></i> {movie.rating}
                  </span>
                  <span className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-sm rounded-full">
                    {catInfo?.name}
                  </span>
                  <span className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-sm rounded-full">
                    {movie.subCategory}
                  </span>
                  <span className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-sm rounded-full">
                    {movie.year}
                  </span>
                  <span className="text-sm text-foreground-500">{movie.duration}</span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-6">
                  {movie.quality.map((q) => (
                    <span key={q} className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                      {q}
                    </span>
                  ))}
                </div>

                <div className="space-y-4 mb-8">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">剧情简介</h3>
                    <p className="text-sm text-foreground-700 leading-relaxed">{movie.description}</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <span className="text-sm text-foreground-500">导演：</span>
                      <span className="text-sm text-foreground-900">{movie.director}</span>
                    </div>
                    <div>
                      <span className="text-sm text-foreground-500">主演：</span>
                      <span className="text-sm text-foreground-900">{movie.cast.join('、')}</span>
                    </div>
                    <div>
                      <span className="text-sm text-foreground-500">片长：</span>
                      <span className="text-sm text-foreground-900">{movie.duration}</span>
                    </div>
                    <div>
                      <span className="text-sm text-foreground-500">更新时间：</span>
                      <span className="text-sm text-foreground-900">{movie.updateInfo}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
                    <i className="ri-play-fill mr-1.5"></i>立即播放
                  </button>
                  <button className="px-6 py-2.5 bg-secondary-500 text-white text-sm rounded-lg hover:bg-secondary-600 transition-colors whitespace-nowrap">
                    <i className="ri-heart-line mr-1.5"></i>收藏
                  </button>
                  <button className="px-6 py-2.5 border border-background-300 text-foreground-700 text-sm rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
                    <i className="ri-share-line mr-1.5"></i>分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedMovies.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-screen-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedMovies.map((rm) => (
                  <Link
                    key={rm.id}
                    to={`/article/${rm.id}`}
                    className="group"
                  >
                    <div className="relative rounded-lg overflow-hidden aspect-[2/3] mb-3">
                      <img
                        src={rm.poster}
                        alt={rm.title}
                        loading="lazy"
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 bg-accent-500 text-white text-[10px] font-medium rounded">
                          {rm.quality[0]}
                        </span>
                      </div>
                      <div className="absolute top-2 right-2">
                        <span className="px-1.5 py-0.5 bg-black/60 text-accent-400 text-[10px] rounded">
                          {rm.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {rm.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                { q: '90影院的画质有哪些选择？', a: `90影院提供${movie.quality.join('、')}等多种画质选择，满足不同设备和网络环境下的观影需求。` },
                { q: '播放是否会缓冲卡顿？', a: '90影院采用先进的CDN加速技术，确保播放流畅不缓冲。即使在高清画质下，也能获得流畅的观看体验。' },
                { q: '需要付费吗？', a: '90影院所有影视资源完全免费在线观看，无需注册登录，无需付费。我们致力于为广大影迷提供优质的免费观影服务。' },
              ].map((faq, idx) => (
                <details key={idx} className="group border border-background-200 rounded-lg">
                  <summary className="flex items-center justify-between px-4 md:px-6 py-4 cursor-pointer text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="px-4 md:px-6 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: '90影院的画质有哪些选择？',
                  acceptedAnswer: { '@type': 'Answer', text: `90影院提供${movie.quality.join('、')}等多种画质选择，满足不同设备和网络环境下的观影需求。` },
                },
                {
                  '@type': 'Question',
                  name: '播放是否会缓冲卡顿？',
                  acceptedAnswer: { '@type': 'Answer', text: '90影院采用先进的CDN加速技术，确保播放流畅不缓冲。即使在高清画质下，也能获得流畅的观看体验。' },
                },
                {
                  '@type': 'Question',
                  name: '需要付费吗？',
                  acceptedAnswer: { '@type': 'Answer', text: '90影院所有影视资源完全免费在线观看，无需注册登录，无需付费。' },
                },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}